// /tax/dta/$country — TH DTA detail. Phase P-TAX-DEEP-DIVE.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getDTA, DTA_COUNTRIES } from "@/data/tax-dta";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Globe2, ChevronRight, Sparkles, FileText, ShieldCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/tax/dta/$country")({
  loader: ({ params }) => {
    const dta = getDTA(params.country);
    if (!dta) throw notFound();
    return { dta };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const d = loaderData.dta;
    const url = `${SITE}/tax/dta/${d.slug}`;
    const title = `DTA ไทย-${d.countryTh} ${d.effective} — อัตรา WHT, Foreign Tax Credit, การใช้สิทธิ์ | NYC`;
    const desc = `สนธิสัญญาภาษีซ้อนระหว่างไทยและ${d.countryTh} (มีผล ${d.effective}) — Dividend ${d.whtDividend}, Interest ${d.whtInterest}, Royalty ${d.whtRoyalty} วิธี Credit Method พร้อมเอกสารและตัวอย่าง — ทักไลน์เพื่อรับเช็กลิสต์เอกสารทันที`;
    const faqs = [
      { q: `อัตรา WHT Dividend ตาม DTA ไทย-${d.countryTh} เท่าไร`, a: d.whtDividend },
      { q: `Foreign Tax Credit ใช้วิธีไหน`, a: d.method === "credit" ? "Credit Method (เครดิตภาษีต่างประเทศ)" : d.method === "exemption" ? "Exemption Method (ยกเว้น)" : "Mixed Method" },
      { q: `DTA นี้มีผลตั้งแต่ปีใด`, a: `ลงนาม ${d.signed} มีผล ${d.effective}` },
      { q: `ใครใช้สิทธิ์ DTA นี้ได้บ้าง`, a: `${d.useCaseTh} และผู้มีรายได้จาก${d.countryTh}` },
    ];
    return {
      meta: [
        { title: ctaTitle(title, "/tax/dta/$country", "th") }, { name: "description", content: ctaDesc(desc, "/tax/dta/$country", "th")},
        { property: "og:title", content: ctaTitle(title, "/tax/dta/$country", "th")}, { property: "og:description", content: ctaDesc(desc, "/tax/dta/$country", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/tax/dta/${d.slug}`, `/en/tax/dta/${d.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", ".quick-answer", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Article",
              headline: title, description: desc, inLanguage: "th",
              author: { "@type": "Organization", name: "NYC Legal & Notary Service", url: SITE },
              publisher: { "@type": "Organization", name: "NYC Legal & Notary Service", url: SITE, logo: { "@type": "ImageObject", url: `${SITE}/logo.png` } },
              datePublished: "2026-06-12", dateModified: "2026-06-12",
              mainEntityOfPage: url,
            }),
          },
          faqPageSchema(faqs),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "ภาษีไทย", item: `${SITE}/tax` },
                { "@type": "ListItem", position: 3, name: `DTA ไทย-${d.countryTh}`, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-3xl font-extrabold">ไม่พบประเทศนี้ใน DTA</h1>
      <Link to="/tax" className="mt-6 inline-block rounded-lg bg-primary px-5 py-3 font-bold text-primary-foreground">กลับหน้าภาษี</Link>
    </main>
  ),
  errorComponent: () => <main className="p-16 text-center">เกิดข้อผิดพลาด</main>,
  component: DTAPageTh,
});

function DTAPageTh() {
  const { dta: d } = Route.useLoaderData();
  const quick = `สนธิสัญญาภาษีซ้อน (DTA) ระหว่างไทยและ${d.countryTh} ลงนามปี ${d.signed} มีผลปี ${d.effective} ใช้วิธี ${d.method === "credit" ? "Credit Method" : d.method === "exemption" ? "Exemption Method" : "Mixed"} ครอบคลุม Dividend ${d.whtDividend}, Interest ${d.whtInterest}, Royalty ${d.whtRoyalty} เพื่อหลีกเลี่ยงการเสียภาษีซ้ำซ้อนสำหรับผู้มีรายได้ใน 2 ประเทศ`;
  const others = DTA_COUNTRIES.filter((x) => x.slug !== d.slug).slice(0, 12);
  return (
    <main className="min-h-screen bg-background text-foreground">
      <nav className="mx-auto max-w-5xl px-4 pt-6 text-xs text-muted-foreground" aria-label="Breadcrumb">
        <Link to="/tax" className="hover:underline">ภาษีไทย</Link>
        <ChevronRight className="mx-1 inline h-3 w-3" />
        <span>DTA ไทย-{d.countryTh}</span>
      </nav>

      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-8 pb-12">
        <div className="mx-auto max-w-5xl">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary">
            <Globe2 className="h-3 w-3" /> Double Tax Agreement
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">DTA ไทย-{d.countryTh} ({d.iso2})</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">{d.notesTh} — เหมาะกับ {d.useCaseTh}</p>

          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">DTA ไทย-{d.countryTh} ครอบคลุมอะไรบ้าง?</h2>
            <p className="direct-answer mt-2 text-sm leading-relaxed sm:text-base">{quick}</p>
          </div>

          <dl className="mt-6 grid max-w-3xl grid-cols-1 gap-0 overflow-hidden rounded-2xl border border-border bg-card text-sm sm:grid-cols-2" data-speakable="true">
            {[
              { k: "ประเทศคู่สนธิสัญญา", v: `${d.countryTh} (${d.iso2})` },
              { k: "ปีที่ลงนาม", v: d.signed },
              { k: "ปีที่มีผล", v: d.effective },
              { k: "Dividend WHT", v: d.whtDividend },
              { k: "Interest WHT", v: d.whtInterest },
              { k: "Royalty WHT", v: d.whtRoyalty },
              { k: "วิธีการ", v: d.method === "credit" ? "Credit Method" : d.method === "exemption" ? "Exemption Method" : "Mixed" },
              { k: "Use Case หลัก", v: d.useCaseTh },
            ].map((r, i, arr) => (
              <div key={r.k} className={`flex justify-between gap-3 px-4 py-2.5 ${i < arr.length - 1 ? "border-b border-border" : ""}`}>
                <dt className="font-medium text-muted-foreground">{r.k}</dt>
                <dd className="text-right font-semibold">{r.v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-5xl grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 text-xl font-bold"><ShieldCheck className="h-5 w-5 text-primary" /> วิธีใช้สิทธิ์ DTA</h2>
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
              <li>ขอ Certificate of Residence (แบบ RO22) จากสรรพากรไทย</li>
              <li>ส่งใบรับรองให้ผู้จ่ายใน{d.countryTh} เพื่อขอลด WHT</li>
              <li>เก็บใบรับรองภาษีต้นทาง (Tax Payment Certificate)</li>
              <li>แปลรับรองใบรับรองและประทับ NAATI/MFA</li>
              <li>ยื่น ภ.ง.ด.90 พร้อมขอเครดิตภาษีต่างประเทศ</li>
            </ol>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 text-xl font-bold"><FileText className="h-5 w-5 text-primary" /> เอกสารที่ต้องใช้</h2>
            <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-muted-foreground">
              <li>Tax Payment Certificate จาก{d.countryTh}</li>
              <li>คำแปลรับรอง (NAATI/MFA/ทนายความ)</li>
              <li>Certificate of Residence ไทย (RO22)</li>
              <li>หลักฐานการรับเงิน (Bank Statement)</li>
              <li>แบบฟอร์ม Tax Return ของ{d.countryTh}</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-xl font-bold">DTA ประเทศอื่นที่น่าสนใจ</h2>
          <div className="mt-4 grid gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {others.map((x) => (
              <Link key={x.slug} to="/tax/dta/$country" params={{ country: x.slug }} className="rounded-lg border border-border bg-card p-3 text-sm hover:border-primary">
                <span className="font-bold">{x.countryTh}</span>
                <div className="mt-1 text-[11px] text-muted-foreground">Div {x.whtDividend}</div>
              </Link>
            ))}
          </div>
          <Link to="/tax" className="mt-8 inline-block text-sm text-primary hover:underline">← กลับหน้าภาษีรวม</Link>
        </div>
      </section>

      <TrustLayer />
    </main>
  );
}
